import React, { useState, useEffect } from 'react';
import {

    Download,
    TrendingUp,
    RefreshCw
} from 'lucide-react';
import api from '../../services/api';
import orderService from '../../services/orderService';

const AdminReports = () => {
    const [viewType, setViewType] = useState('monthly');
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const [stats, setStats] = useState({
        revenue: 0,
        itemsCount: 0,
        avgBasket: 0,
        visitedProducts: []
    });

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                // Fetch all orders and products (use admin API)
                const [ordersData, productsRes] = await Promise.all([
                    orderService.getAllOrders(),
                    api.get('/api/products/')
                ]);

                const pMap = {};
                productsRes.data.forEach(p => pMap[p.id] = p.name);
                setProducts(pMap);
                setOrders(ordersData);
            } catch (error) {
                console.error('Error loading reports data:', error);
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, []);

    useEffect(() => {
        if (orders.length === 0) return;

        const now = new Date();
        const filteredOrders = orders.filter(order => {
            const orderDate = new Date(order.created_at);
            if (order.status?.toLowerCase() === 'cancelled') return false;
            // Only count WhatsApp clicks as "visits" for this report
            if (order.payment_method !== 'WhatsApp') return false;

            switch (viewType) {
                case 'daily':
                    return orderDate.toDateString() === now.toDateString();
                case 'weekly':
                    const weekAgo = new Date();
                    weekAgo.setDate(now.getDate() - 7);
                    return orderDate >= weekAgo;
                case 'monthly':
                    return orderDate.getMonth() === now.getMonth() && orderDate.getFullYear() === now.getFullYear();
                default:
                    return true;
            }
        });

        const revenue = filteredOrders.reduce((sum, o) => sum + parseFloat(o.total_price), 0);

        let itemsCount = 0;
        const productVisits = {};

        filteredOrders.forEach(order => {
            order.items?.forEach(item => {
                itemsCount += item.quantity;
                if (!productVisits[item.product]) {
                    productVisits[item.product] = {
                        id: item.product,
                        name: products[item.product] || `Produit #${item.product}`,
                        qty: 0,
                        total: 0,
                        clients: []
                    };
                }
                productVisits[item.product].qty += item.quantity;
                productVisits[item.product].total += parseFloat(item.price) * item.quantity;
                productVisits[item.product].clients.push({
                    name: order.full_name,
                    phone: order.phone,
                    date: new Date(order.created_at).toLocaleDateString()
                });
            });
        });

        const visitedProducts = Object.values(productVisits).sort((a, b) => b.qty - a.qty);

        setStats({
            revenue,
            itemsCount,
            avgBasket: filteredOrders.length > 0 ? revenue / filteredOrders.length : 0,
            visitedProducts
        });
    }, [viewType, orders, products]);

    const viewLabels = {
        daily: 'Aujourd\'hui',
        weekly: 'Cette Semaine',
        monthly: 'Ce Mois'
    };

    return (
        <div className="space-y-10">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-black text-green-950 mb-2">Rapports d'Engagement</h1>
                    <p className="text-slate-500">Suivez les produits qui intéressent vos clients sur WhatsApp.</p>
                </div>
                <div className="flex bg-white p-2 rounded-2xl border border-slate-100 shadow-sm">
                    {['daily', 'weekly', 'monthly'].map((type) => (
                        <button
                            key={type}
                            onClick={() => setViewType(type)}
                            className={`px-6 py-3 rounded-xl font-bold text-sm transition-all ${viewType === type ? 'bg-brand text-white shadow-lg' : 'text-slate-500 hover:bg-slate-50'
                                }`}
                        >
                            {type === 'daily' ? 'Journalier' : type === 'weekly' ? 'Hebdomadaire' : 'Mensuel'}
                        </button>
                    ))}
                </div>
            </div>

            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <RefreshCw className="w-10 h-10 text-brand animate-spin mb-4" />
                    <p className="text-slate-500 font-bold">Analyse des données en cours...</p>
                </div>
            ) : (
                <>
                    {/* Quick Stats Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                            <p className="text-slate-400 font-bold text-xs uppercase tracking-[0.2em] mb-4">Articles Visités (WhatsApp)</p>
                            <div className="flex items-center justify-between">
                                <h3 className="text-3xl font-black text-green-950">{stats.itemsCount}</h3>
                                <div className="w-12 h-12 bg-sky-100 text-sky-600 rounded-2xl flex items-center justify-center font-black text-xs">
                                    <TrendingUp className="w-5 h-5" />
                                </div>
                            </div>
                        </div>
                        <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                            <p className="text-slate-400 font-bold text-xs uppercase tracking-[0.2em] mb-4">Valeur Potentielle</p>
                            <div className="flex items-center justify-between">
                                <h3 className="text-3xl font-black text-green-950">{Math.floor(stats.revenue).toLocaleString()} <span className="text-sm font-bold text-slate-400">Fcfa</span></h3>
                                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center font-black text-xs">
                                    Total
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Sales List */}
                    <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-8">
                        <div className="flex items-center justify-between mb-8 border-b border-slate-50 pb-6">
                            <h3 className="text-xl font-black text-green-950">Détails des visites par produit ({viewLabels[viewType]})</h3>
                            <button
                                onClick={() => {
                                    const csvData = [
                                        { item: 'RAPPORT D\'ENGAGEMENT WHATSAPP', detail: viewLabels[viewType].toUpperCase(), info: '' },
                                        { item: '-------------------------------', detail: '-----------------', info: '----------' },
                                        { item: 'Articles Visités (WhatsApp)', detail: stats.itemsCount, info: 'clics' },
                                        { item: 'Valeur Potentielle Totale', detail: Math.floor(stats.revenue).toLocaleString(), info: 'Fcfa' },
                                        { item: '-------------------------------', detail: '-----------------', info: '----------' },
                                        { item: '', detail: '', info: '' },
                                        { item: 'DÉTAIL PAR PRODUIT ET CLIENT', detail: '', info: '' },
                                        { item: '-------------------------------', detail: '', info: '' }
                                    ];

                                    (stats.visitedProducts || []).forEach(p => {
                                        csvData.push({ item: p.name, detail: `${p.qty} visites`, info: '' });
                                        p.clients.forEach(c => {
                                            csvData.push({ item: `  ↳ Client: ${c.name}`, detail: c.phone, info: c.date });
                                        });
                                        csvData.push({ item: '', detail: '', info: '' });
                                    });

                                    const headers = [
                                        { label: 'Indicateur / Produit / Client', key: 'item' },
                                        { label: 'Valeur / Téléphone', key: 'detail' },
                                        { label: 'Infos / Date', key: 'info' }
                                    ];
                                    import('../../utils/exportUtils').then(utils => {
                                        utils.downloadCSV(csvData, `rapport_complet_${viewType}_${new Date().toISOString().split('T')[0]}`, headers);
                                    });
                                }}
                                className="flex items-center gap-2 text-sm font-bold text-brand hover:underline"
                            >
                                <Download className="w-4 h-4" />
                                Exporter CSV détaillé
                            </button>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="text-left py-4 border-b border-slate-100">
                                        <th className="pb-4 text-xs font-black text-slate-400 uppercase tracking-widest">Article / Client</th>
                                        <th className="pb-4 text-xs font-black text-slate-400 uppercase tracking-widest text-center">Nombre de clics</th>
                                        <th className="pb-4 text-xs font-black text-slate-400 uppercase tracking-widest text-right">Détails Clients</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {(stats.visitedProducts || []).length > 0 ? stats.visitedProducts.map((p) => (
                                        <tr key={p.id} className="group hover:bg-slate-50/50 transition-all">
                                            <td className="py-6">
                                                <p className="font-bold text-green-950">{p.name}</p>
                                            </td>
                                            <td className="py-6 text-center">
                                                <span className="px-4 py-2 bg-slate-100 text-slate-600 font-black text-sm rounded-xl">
                                                    {p.qty}
                                                </span>
                                            </td>
                                            <td className="py-6 text-right">
                                                <div className="space-y-1">
                                                    {p.clients.slice(0, 3).map((c, idx) => (
                                                        <p key={idx} className="text-xs font-medium text-slate-500">
                                                            {c.name} ({c.phone})
                                                        </p>
                                                    ))}
                                                    {p.clients.length > 3 && (
                                                        <p className="text-[10px] text-brand font-bold">+{p.clients.length - 3} autres clients</p>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="3" className="py-10 text-center text-slate-400 font-medium">
                                                Aucune visite enregistrée pour cette période.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminReports;
